import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Trash2, RefreshCw, ChevronDown, ChevronUp, Target as TargetIcon, Wand2 } from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Line, LineChart, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const now = new Date();
const fmt = (n, d = 1) => (n ?? 0).toLocaleString("en-ZA", { minimumFractionDigits: d, maximumFractionDigits: d });
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// ----------------------------------------------------------------------------
// Date-range presets (custom range filter)
// ----------------------------------------------------------------------------
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d)   { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function isoDate(d) {
  // Build local-tz date string (avoid toISOString() shifting SAST midnight to UTC and
  // leaking the previous day into "this month" / "last month" presets).
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function startOfQuarter(d) { return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1); }
function endOfQuarter(d)   { return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3 + 3, 0); }

const RANGE_PRESETS = [
  {
    id: "this_month", label: "This month",
    range: () => ({ from: isoDate(startOfMonth(now)), to: isoDate(endOfMonth(now)) }),
    suggestedGranularity: "daily",
  },
  {
    id: "last_month", label: "Last month",
    range: () => {
      const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return { from: isoDate(startOfMonth(d)), to: isoDate(endOfMonth(d)) };
    },
    suggestedGranularity: "daily",
  },
  {
    id: "this_quarter", label: "This quarter",
    range: () => ({ from: isoDate(startOfQuarter(now)), to: isoDate(endOfQuarter(now)) }),
    suggestedGranularity: "weekly",
  },
  {
    id: "this_year", label: "This year",
    range: () => ({ from: `${now.getFullYear()}-01-01`, to: `${now.getFullYear()}-12-31` }),
    suggestedGranularity: "monthly",
  },
  {
    id: "last_year", label: "Last year",
    range: () => ({ from: `${now.getFullYear() - 1}-01-01`, to: `${now.getFullYear() - 1}-12-31` }),
    suggestedGranularity: "monthly",
  },
];

const GRANULARITIES = [
  { id: "daily",   label: "Day"   },
  { id: "weekly",  label: "Week"  },
  { id: "monthly", label: "Month" },
];

// Pretty label for X-axis
function periodLabel(x, granularity) {
  if (granularity === "monthly") {
    const [y, m] = x.split("-").map(Number);
    return `${MONTHS[m - 1]} '${String(y).slice(-2)}`;
  }
  if (granularity === "weekly") return x.replace("-W", " W");
  // daily YYYY-MM-DD → DD MMM
  const d = new Date(x);
  return `${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]}`;
}

// ----------------------------------------------------------------------------
// Page
// ----------------------------------------------------------------------------
export default function SalesTargets() {
  const [presetId, setPresetId] = useState("this_month");
  const [from, setFrom] = useState(RANGE_PRESETS[0].range().from);
  const [to, setTo] = useState(RANGE_PRESETS[0].range().to);
  const [granularity, setGranularity] = useState(RANGE_PRESETS[0].suggestedGranularity);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showTargetsTable, setShowTargetsTable] = useState(false);
  const [showFyPace, setShowFyPace] = useState(false);
  const [showDrilldown, setShowDrilldown] = useState(false);

  // Apply preset
  const applyPreset = (p) => {
    setPresetId(p.id);
    const r = p.range();
    setFrom(r.from); setTo(r.to);
    setGranularity(p.suggestedGranularity);
  };

  const load = async () => {
    if (!from || !to) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.get(`/sales-targets/range?date_from=${from}&date_to=${to}&granularity=${granularity}`);
      setData(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to, granularity]);

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.series.map((s) => ({
      ...s,
      label: periodLabel(s.x, granularity),
    }));
  }, [data, granularity]);

  return (
    <div data-testid="sales-targets-page">
      <PageHeader
        eyebrow="Sales"
        title="Sales &amp; Targets"
        description="Track actual vs target volume (kg) for any date range. Per-period view (not cumulative); switch granularity between day/week/month. Drill down by blend for the same range. Targets are entered per month — apportioned to weeks proportionally, but NOT split per day."
      />

      {error && <div className="bg-destructive/10 border border-destructive/30 rounded p-3 text-sm mb-4" data-testid="targets-error">{error}</div>}

      {/* Date range filter */}
      <div className="bg-card border border-border rounded p-4 mb-5">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[280px]">
            <p className="label-tiny mb-2">Date range</p>
            <div className="flex flex-wrap gap-1.5" data-testid="range-presets">
              {RANGE_PRESETS.map((p) => (
                <button key={p.id} onClick={() => applyPreset(p)}
                  data-testid={`preset-${p.id}`}
                  className={`px-3 py-1.5 rounded-full border text-xs font-semibold transition ${
                    presetId === p.id
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card text-muted-foreground border-border hover:bg-secondary"
                  }`}>
                  {p.label}
                </button>
              ))}
              <span className={`px-3 py-1.5 rounded-full border text-xs font-semibold ${
                presetId === "custom" ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border"
              }`}>Custom</span>
            </div>
          </div>
          <div>
            <p className="label-tiny mb-2">From</p>
            <input type="date" value={from} data-testid="range-from"
              onChange={(e) => { setFrom(e.target.value); setPresetId("custom"); }}
              className="px-3 py-1.5 border border-border rounded text-sm font-mono" />
          </div>
          <div>
            <p className="label-tiny mb-2">To</p>
            <input type="date" value={to} data-testid="range-to"
              onChange={(e) => { setTo(e.target.value); setPresetId("custom"); }}
              className="px-3 py-1.5 border border-border rounded text-sm font-mono" />
          </div>
          <div>
            <p className="label-tiny mb-2">Group by</p>
            <div className="inline-flex border border-border rounded overflow-hidden text-xs font-semibold" data-testid="granularity-selector">
              {GRANULARITIES.map((g, i) => (
                <button key={g.id} onClick={() => setGranularity(g.id)}
                  data-testid={`granularity-${g.id}`}
                  className={`px-3 py-1.5 ${granularity === g.id ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-secondary"} ${i > 0 ? "border-l border-border" : ""}`}>
                  {g.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* KPIs (kg only — Rand removed per spec) */}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="targets-kpis">
          <KPI label="Actual (kg)" value={`${fmt(data.summary.kg_actual)} kg`} />
          <KPI label="Target (kg)" value={`${fmt(data.summary.kg_target)} kg`}
               hint={data.granularity === "daily"
                      ? "Sum of monthly targets overlapping the range (not split per day)"
                      : "Monthly targets, apportioned proportionally"} />
          <KPI label="Variance" value={`${data.summary.kg_variance >= 0 ? "+" : ""}${fmt(data.summary.kg_variance)} kg`}
               accent={data.summary.kg_variance >= 0 ? "ok" : "warn"} />
          <KPI label="Achievement"
               value={data.summary.achievement_pct == null ? "—" : `${fmt(data.summary.achievement_pct)} %`}
               hint={data.summary.kg_target > 0 ? "Actual ÷ target × 100" : "No target set"}
               accent={data.summary.achievement_pct >= 100 ? "ok" : data.summary.achievement_pct >= 75 ? "warn" : "critical"} />
        </div>
      )}

      {/* Per-period bar chart */}
      <div className="bg-card border border-border rounded p-5 mb-5" data-testid="period-chart">
        <h3 className="font-display text-lg font-bold mb-3">
          Per-{granularity === "daily" ? "day" : granularity === "weekly" ? "week" : "month"} actual vs target
        </h3>
        {busy && !data && <p className="text-sm text-muted-foreground py-12 text-center">Loading…</p>}
        {data && data.series.length === 0 && (
          <p className="text-sm text-muted-foreground py-12 text-center">No data in range.</p>
        )}
        {data && data.series.length > 0 && (
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(40 21% 87%)" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v.toFixed(0)} kg`} />
                <Tooltip
                  formatter={(v, name) => [v == null ? "—" : `${fmt(v)} kg`, name === "kg_actual" ? "Actual" : "Target"]}
                  labelFormatter={(l) => l}
                  contentStyle={{ background: "hsl(40 21% 96%)", border: "1px solid hsl(40 21% 87%)", borderRadius: 4, fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }}
                  formatter={(v) => v === "kg_actual" ? "Actual" : "Target"} />
                <Line type="monotone" dataKey="kg_target" stroke="hsl(132, 18%, 36%)" strokeWidth={2}
                  strokeDasharray="6 3" connectNulls={false}
                  dot={{ r: 3, fill: "hsl(132, 18%, 36%)" }} activeDot={{ r: 5 }} />
                <Line type="monotone" dataKey="kg_actual" stroke="hsl(20, 39%, 39%)" strokeWidth={2.5}
                  dot={{ r: 3, fill: "hsl(20, 39%, 39%)" }} activeDot={{ r: 5 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Drill-down by blend (kg only) — collapsible */}
      <CollapsibleSection
        testId="drilldown-section"
        open={showDrilldown}
        onToggle={() => setShowDrilldown(!showDrilldown)}
        title="By blend / SKU — drill-down for selected range"
        subtitle={data ? `${data.by_blend?.length || 0} blend(s) · ${fmt(data.summary?.kg_actual || 0)} kg total` : "kg-only breakdown by blend / SKU"}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="drilldown-table">
            <thead className="bg-secondary">
              <tr>
                <th className="text-left p-3 label-tiny">Blend / SKU</th>
                <th className="text-right p-3 label-tiny">Kg sold</th>
                <th className="text-right p-3 label-tiny">Share</th>
              </tr>
            </thead>
            <tbody>
              {data?.by_blend?.length === 0 && (
                <tr><td colSpan={3} className="p-8 text-center text-muted-foreground text-xs">No sales recorded in range.</td></tr>
              )}
              {data?.by_blend?.map((r) => {
                const share = data.summary.kg_actual > 0 ? (r.kg / data.summary.kg_actual * 100) : 0;
                return (
                  <tr key={r.label} className="border-t border-border">
                    <td className="p-3">{r.label}</td>
                    <td className="p-3 text-right font-mono tabular-nums">{fmt(r.kg)} kg</td>
                    <td className="p-3 text-right font-mono tabular-nums text-muted-foreground">{fmt(share)} %</td>
                  </tr>
                );
              })}
              {data?.by_blend?.length > 0 && (
                <tr className="bg-secondary border-t border-border font-semibold">
                  <td className="p-3">Total</td>
                  <td className="p-3 text-right font-mono tabular-nums">{fmt(data.summary.kg_actual)} kg</td>
                  <td className="p-3 text-right font-mono tabular-nums">100.0 %</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CollapsibleSection>

      {/* Monthly targets editor */}
      <CollapsibleSection
        testId="monthly-targets-section"
        open={showTargetsTable}
        onToggle={() => setShowTargetsTable(!showTargetsTable)}
        title="Monthly targets · enter & manage"
        subtitle="Per-month target volumes (kg). Distributed proportionally to days/weeks when grouped finer than monthly."
      >
        <MonthlyTargetsManager onChange={() => load()} />
      </CollapsibleSection>

      {/* FY pace (cumulative — kept for historical analysis) */}
      <CollapsibleSection
        testId="fy-pace-section"
        open={showFyPace}
        onToggle={() => setShowFyPace(!showFyPace)}
        title="FY pace · cumulative view"
        subtitle="Long-form view of full-FY pace vs targets. Useful for sanity-checking against the FY-level Min / Growth / Expand multipliers."
      >
        <FyPaceChart />
      </CollapsibleSection>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Sub-components
// ----------------------------------------------------------------------------
function KPI({ label, value, hint, accent }) {
  const tone = {
    ok:       "border-[hsl(132,18%,36%)]/30 bg-[hsl(132,18%,36%)]/5",
    warn:     "border-amber-300 bg-amber-50",
    critical: "border-destructive/40 bg-destructive/5",
  }[accent] || "border-border bg-card";
  return (
    <div className={`rounded p-4 border ${tone}`}>
      <p className="label-tiny mb-1.5">{label}</p>
      <p className="font-display text-2xl font-bold tabular-nums">{value}</p>
      {hint && <p className="text-[10px] text-muted-foreground mt-1 font-mono">{hint}</p>}
    </div>
  );
}

function CollapsibleSection({ testId, open, onToggle, title, subtitle, children }) {
  return (
    <div className="bg-card border border-border rounded mb-5" data-testid={testId}>
      <button onClick={onToggle} className="w-full px-5 py-3 flex items-center gap-3 text-left hover:bg-secondary/40">
        {open ? <ChevronUp className="w-4 h-4 text-primary" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        <div className="flex-1">
          <h3 className="font-display text-base font-bold">{title}</h3>
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        </div>
        <span className="text-xs text-muted-foreground">{open ? "Hide" : "Show"}</span>
      </button>
      {open && <div className="px-5 py-4 border-t border-border">{children}</div>}
    </div>
  );
}

// FY-month order: Mar (fy-1) → Dec (fy-1) → Jan (fy) → Feb (fy)
// Each entry = [calendarYearOffset, month] where 0 = fy-1, 1 = fy
const FY_MONTH_ORDER = [
  [0, 3], [0, 4], [0, 5], [0, 6], [0, 7], [0, 8],
  [0, 9], [0, 10], [0, 11], [0, 12], [1, 1], [1, 2],
];

const fyOptions = () => {
  const m = now.getMonth() + 1;
  const y = now.getFullYear();
  const cur = m >= 3 ? y + 1 : y;
  return [cur - 1, cur, cur + 1];
};

function MonthlyTargetsManager({ onChange }) {
  const [fyYear, setFyYear] = useState(fyOptions()[1]);
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [autofillBusy, setAutofillBusy] = useState(false);
  const [autofillMsg, setAutofillMsg] = useState("");
  const [autofillError, setAutofillError] = useState("");

  // FY thresholds (min/growth/expand) — drive the 3 reference lines on the
  // cumulative chart and the inventory forecast. Stored per FY; sensible
  // default 110/115/120 (+10%/+15%/+20% over prior FY actuals).
  const [thresholds, setThresholds] = useState({ min_pct: 110, growth_pct: 115, expand_pct: 120 });
  const [thresholdsSaved, setThresholdsSaved] = useState(true);
  const [thresholdsBusy, setThresholdsBusy] = useState(false);

  const loadThresholds = async () => {
    const { data } = await api.get("/fy-targets");
    const row = data.find((d) => d.fy_year === fyYear);
    setThresholds(row ? {
      min_pct: row.min_pct, growth_pct: row.growth_pct, expand_pct: row.expand_pct,
    } : { min_pct: 110, growth_pct: 115, expand_pct: 120 });
    setThresholdsSaved(true);
  };
  const saveThresholds = async () => {
    setThresholdsBusy(true);
    try {
      await api.put("/fy-targets", { fy_year: fyYear, ...thresholds, notes: "" });
      setThresholdsSaved(true);
      onChange?.();
    } finally { setThresholdsBusy(false); }
  };
  useEffect(() => { loadThresholds(); /* eslint-disable-next-line */ }, [fyYear]);

  const load = async () => {
    const { data } = await api.get(`/sales-targets?fy_year=${fyYear}`);
    setList(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fyYear]);

  const monthRow = (y, m) => list.find((t) => t.year === y && t.month === m && !t.blend_id) || null;

  const save = async (form) => {
    await api.post("/sales-targets", { ...form, blend_id: null });
    setEditing(null);
    await load();
    onChange?.();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this monthly target?")) return;
    await api.delete(`/sales-targets/${id}`);
    await load();
    onChange?.();
  };

  const autofill = async (growthPct) => {
    const existing = list.filter((t) => !t.blend_id).length;
    const action = existing > 0
      ? `Auto-fill FY${fyYear} targets at +${growthPct}% over FY${fyYear - 1} actuals?\n\n` +
        `${existing} month(s) already have a target — they will be OVERWRITTEN. Continue?`
      : `Auto-fill FY${fyYear} targets at +${growthPct}% over FY${fyYear - 1} actuals?`;
    if (!window.confirm(action)) return;
    setAutofillBusy(true); setAutofillMsg(""); setAutofillError("");
    try {
      const { data } = await api.post("/sales-targets/autofill", {
        fy_year: fyYear, growth_pct: growthPct, overwrite: true,
      });
      setAutofillMsg(`Filled ${data.written} month(s) at +${growthPct}% (${data.prior_label} actuals × ${data.multiplier}).`);
      await load();
      onChange?.();
    } catch (e) {
      setAutofillError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setAutofillBusy(false); }
  };

  const totalTarget = list.filter((t) => !t.blend_id).reduce((s, t) => s + (t.target_kg || 0), 0);

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <div>
          <label className="label-tiny mb-1 block">Financial year</label>
          <select value={fyYear} onChange={(e) => setFyYear(parseInt(e.target.value))}
            data-testid="targets-fy"
            className="px-3 py-1.5 bg-white border border-border rounded text-sm font-mono">
            {[fyYear - 2, fyYear - 1, fyYear, fyYear + 1].map((y) => (
              <option key={y} value={y}>FY {y} (Mar {y - 1} – Feb {y})</option>
            ))}
          </select>
        </div>
        <div className="text-xs font-mono text-muted-foreground" data-testid="fy-target-total">
          FY total: <strong className="text-foreground">{fmt(totalTarget)} kg</strong>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <span className="label-tiny mr-1">Auto-fill from FY{fyYear - 1} actuals:</span>
          {[
            { pct: 10, label: "Minimum" },
            { pct: 15, label: "Growth" },
            { pct: 20, label: "Expand" },
          ].map(({ pct, label }) => (
            <button key={pct} type="button" onClick={() => autofill(pct)} disabled={autofillBusy}
              data-testid={`autofill-${pct}`}
              title={`Apply +${pct}% (${label}) to every FY${fyYear - 1} monthly actual`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border rounded text-xs font-semibold hover:bg-secondary disabled:opacity-60">
              <Wand2 className="w-3 h-3" />
              <span>{label}</span>
              <span className="font-mono text-muted-foreground">+{pct}%</span>
            </button>
          ))}
        </div>
      </div>

      {/* FY threshold editor — controls the 3 reference lines on the cumulative chart
          and the projected demand bands in the Inventory Forecast. Stored per FY. */}
      <div className="mb-4 bg-secondary/40 border border-border rounded p-3"
           data-testid="fy-thresholds">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <p className="text-xs font-semibold">FY{fyYear} target curves (% of FY{fyYear - 1} actuals)</p>
          {[
            { key: "min_pct",    label: "Minimum",  testid: "fy-min-pct" },
            { key: "growth_pct", label: "Growth",   testid: "fy-growth-pct" },
            { key: "expand_pct", label: "Expand",   testid: "fy-expand-pct" },
          ].map(({ key, label, testid }) => (
            <label key={key} className="inline-flex items-center gap-1.5 text-xs">
              <span className="font-mono text-muted-foreground w-16">{label}</span>
              <input
                type="number" min="0" max="500" step="1"
                value={thresholds[key]}
                onChange={(e) => {
                  setThresholds({ ...thresholds, [key]: parseFloat(e.target.value || 0) });
                  setThresholdsSaved(false);
                }}
                data-testid={testid}
                className="w-20 px-2 py-1 bg-white border border-border rounded text-sm font-mono text-right"
              />
              <span className="text-muted-foreground">%</span>
              <span className="text-[10px] text-muted-foreground font-mono">
                ({thresholds[key] >= 100 ? "+" : ""}{(thresholds[key] - 100).toFixed(0)}%)
              </span>
            </label>
          ))}
          <button onClick={saveThresholds} disabled={thresholdsSaved || thresholdsBusy}
            data-testid="fy-thresholds-save"
            className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs font-semibold hover:bg-[hsl(20,41%,32%)] disabled:opacity-50">
            {thresholdsBusy ? "Saving…" : thresholdsSaved ? "Saved" : "Save thresholds"}
          </button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-2">
          110 = +10% over last FY; 100 = same as last FY. Changes apply to all reference lines and the Inventory Forecast.
        </p>
      </div>
      {autofillMsg && <p className="text-xs text-[hsl(132,18%,36%)] mb-2 font-mono" data-testid="autofill-msg">{autofillMsg}</p>}
      {autofillError && <p className="text-xs text-destructive mb-2" data-testid="autofill-error">{autofillError}</p>}
      <table className="w-full text-sm" data-testid="monthly-targets-table">
        <thead className="bg-secondary">
          <tr>
            <th className="text-left p-2 label-tiny">Month (FY order)</th>
            <th className="text-right p-2 label-tiny">Target (kg)</th>
            <th className="text-left p-2 label-tiny">Notes</th>
            <th className="text-right p-2 label-tiny w-24">Action</th>
          </tr>
        </thead>
        <tbody>
          {FY_MONTH_ORDER.map(([offset, m]) => {
            const cy = fyYear - 1 + offset;
            const row = monthRow(cy, m);
            return (
              <tr key={`${cy}-${m}`} className="border-t border-border">
                <td className="p-2 font-medium">{MONTHS[m - 1]} {cy}</td>
                <td className="p-2 text-right font-mono tabular-nums">
                  {row ? fmt(row.target_kg) + " kg" : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="p-2 text-xs text-muted-foreground truncate max-w-[300px]">{row?.notes || "—"}</td>
                <td className="p-2 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => setEditing(row || { year: cy, month: m, target_kg: 0, notes: "" })}
                      data-testid={`target-edit-${cy}-${m}`}
                      className="px-2 py-1 text-xs border border-border rounded hover:bg-secondary">
                      {row ? "Edit" : "Set"}
                    </button>
                    {row && (
                      <button onClick={() => remove(row.id)} data-testid={`target-delete-${cy}-${m}`}
                        className="p-1 text-destructive border border-destructive/30 rounded hover:bg-destructive/10">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {editing && <TargetModal form={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function TargetModal({ form: initial, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await onSave({ year: form.year, month: form.month, target_kg: parseFloat(form.target_kg) || 0, notes: form.notes || "" });
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4" data-testid="target-edit-modal">
      <form onSubmit={submit} className="bg-card border border-border rounded shadow-xl max-w-sm w-full p-5 space-y-3">
        <h3 className="font-display text-lg font-bold flex items-center gap-2">
          <TargetIcon className="w-4 h-4 text-primary" /> {MONTHS[form.month - 1]} {form.year} target
        </h3>
        <div>
          <label className="label-tiny mb-1 block">Target (kg)</label>
          <input type="number" step="0.1" min="0" required value={form.target_kg}
            onChange={(e) => setForm({ ...form, target_kg: e.target.value })}
            data-testid="target-kg"
            className="w-full px-3 py-2 border border-border rounded font-mono" />
        </div>
        <div>
          <label className="label-tiny mb-1 block">Notes</label>
          <textarea rows="2" value={form.notes || ""}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="target-notes"
            className="w-full px-3 py-2 border border-border rounded text-xs" />
        </div>
        {err && <p className="text-xs text-destructive">{err}</p>}
        <div className="flex gap-2 justify-end pt-2 border-t border-border">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-border rounded text-sm hover:bg-secondary">Cancel</button>
          <button type="submit" disabled={busy} data-testid="target-submit"
            className="px-4 py-2 bg-primary text-primary-foreground rounded text-sm font-semibold hover:bg-[hsl(20,41%,32%)]">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

// --------------- FY pace (cumulative) ---------------
function FyPaceChart() {
  const [fyYear, setFyYear] = useState(fyOptions()[1]);
  const [granularity, setGranularity] = useState("monthly");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.get(`/sales/trend?fy_year=${fyYear}&granularity=${granularity}`);
      setData(data);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fyYear, granularity]);

  const series = data?.series || [];

  return (
    <div data-testid="fy-pace-chart">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <label className="label-tiny">Financial year</label>
        <select value={fyYear} onChange={(e) => setFyYear(parseInt(e.target.value))}
          data-testid="fy-pace-year"
          className="px-3 py-1.5 bg-white border border-border rounded text-sm font-mono">
          {fyOptions().map((y) => <option key={y} value={y}>FY {y}</option>)}
        </select>
        <label className="label-tiny ml-3">Granularity</label>
        <div className="inline-flex border border-border rounded overflow-hidden text-xs font-semibold">
          {["daily", "weekly", "monthly"].map((g, i) => (
            <button key={g} onClick={() => setGranularity(g)}
              className={`px-3 py-1.5 ${granularity === g ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-secondary"} ${i > 0 ? "border-l border-border" : ""}`}>
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          ))}
        </div>
        <button onClick={load} className="ml-auto inline-flex items-center gap-1 px-2 py-1 text-xs border border-border rounded hover:bg-secondary">
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      {err && <p className="text-destructive text-xs mb-3">{err}</p>}
      {series.length > 0 && (
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={series} margin={{ top: 10, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(40 21% 87%)" />
              <XAxis dataKey="x" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => `${fmt(v)} kg`} contentStyle={{ background: "hsl(40 21% 96%)", border: "1px solid hsl(40 21% 87%)", borderRadius: 4, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="actual_kg"  stroke="hsl(20, 39%, 39%)"  name="Actual"  strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="prior_cum_kg" stroke="hsl(220, 30%, 50%)" strokeWidth={2} strokeDasharray="6 3" name={`FY ${fyYear - 1} actual`} dot={false} />
              <Line type="monotone" dataKey="target_min_kg"    stroke="hsl(132, 18%, 36%)" strokeDasharray="4 3" name="Min" dot={false} />
              <Line type="monotone" dataKey="target_growth_kg" stroke="hsl(31, 65%, 53%)"  strokeDasharray="4 3" name="Growth" dot={false} />
              <Line type="monotone" dataKey="target_expand_kg" stroke="hsl(0, 60%, 50%)"   strokeDasharray="4 3" name="Expand" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
